import Layout from '@components/layout'
import { homePageModuleQuery } from '@data/sanity/queries/page'
import { SanityHomePage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getHomePage } from '@lib/sanity/page'
import { getOrganizationSchema } from '@lib/schema/organization'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getWebPageSchema, getWebSiteSchema } from '@lib/schema/web'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type HomePageProps = BasePageProps & {
  page: SanityHomePage
  site: SanitySiteFragment
}

const HomePage = ({ draftMode, locale, page, site }: HomePageProps) => {
  const schemas = useMemo(() => {
    const organizationSchema = getOrganizationSchema(
      site.schemaSettings?.organization,
    )
    const webSiteSchema = getWebSiteSchema(site)
    const webPageSchema = getWebPageSchema(page, site)
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [
      organizationSchema,
      webSiteSchema,
      webPageSchema,
      ...videoObjectSchemas,
    ]
  }, [page, site])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && (
        <Modules modules={page.modules} heroLogo={page.heroLogo} />
      )}

      {draftMode && (
        <PreviewModules
          modules={page.modules}
          heroLogo={page.heroLogo}
          query={homePageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<HomePageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const homePage = await getHomePage(sanityClient, context.locale)

  if (!homePage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(homePage.page),
      site: getNonNullValues(homePage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default HomePage
